import React, { useEffect, useState } from 'react';
import CrossCombinationView from './CrossCombinationView';
import TakeProfitView from './TakeProfitView';
import TopGainersView from './TopGainersView';
import TopLosersView from './TopLosersView';
import TopAdxTrendView from './TopAdxTrendView';
import TopVolumeView from './TopVolumeView';
import TrendCombinationView from './TrendCombinationView';
import './SignalDashboard.css';
import GlobalFilterBar from './GlobalFilterBar';
import { buildScopedApiUrl, ensureArray, getApiHeaders } from "./config";
import RuleFilterPanel from "./RuleFilterPanel";
import { useWorkspace } from './workspace/WorkspaceContext';


function SignalDashboard() {
  const { workspace } = useWorkspace();
  const [signals, setSignals] = useState([]);
  const [filteredSignals, setFilteredSignals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedContext, setSelectedContext] = useState('gainers');

  // Multi-select global filters
  const [selectedTimeFrames, setSelectedTimeFrames] = useState([]);
  const [selectedSymbols, setSelectedSymbols] = useState([]);

  // Interested Symbols (user watchlist)
  const [interestedSymbols, setInterestedSymbols] = useState([]);
  const [newInterestedSymbol, setNewInterestedSymbol] = useState('');
  const [showInterestedOnly, setShowInterestedOnly] = useState(false);

  // Active positions symbols
  const [activePositionSymbols, setActivePositionSymbols] = useState([]);
  const [showActivePositionsOnly, setShowActivePositionsOnly] = useState(false);

  // Remember previously selected symbols before toggles
  const [prevSelectedSymbols, setPrevSelectedSymbols] = useState([]);

    // Rule based symbols (global)
  const [ruleEnabled, setRuleEnabled] = useState(false);
  const [ruleQualifiedSymbols, setRuleQualifiedSymbols] = useState([]);



  /* ===========================
     FETCH SIGNAL DATA
  =========================== */
  useEffect(() => {
    fetch(
      buildScopedApiUrl('/api/bot/ui/get/getMultiTimeFrameTradingSignalsGroupBySymbol', workspace),
      {
        headers: getApiHeaders(),
      }
    )
      .then((res) => res.json())
      .then((data) => {
        setSignals(ensureArray(data));
        setLoading(false);
      })
      .catch(() => {
        setError('Failed to load data. Please try again later.');
        setLoading(false);
      });
  }, [workspace]);

  /* ===========================
     FETCH ACTIVE POSITIONS
  =========================== */
  useEffect(() => {
    fetch(
      buildScopedApiUrl('/api/bot/ui/get/getSignalRequests', workspace),
      {
        headers: getApiHeaders(),
      }
    )
      .then((res) => res.json())
      .then((rows) => {
        const safeRows = ensureArray(rows);
        const unique = Array.from(
          new Set(safeRows.map((r) => r.symbol).filter(Boolean))
        );
        setActivePositionSymbols(unique);
      })
      .catch(() => {});
  }, [workspace]);

  /* ===========================
     FETCH INTERESTED SYMBOLS (from UpcomingTradeSetupPlan)
  =========================== */
  useEffect(() => {
    fetch(
      buildScopedApiUrl('/api/bot/ui/get/upcoming-plan/getInterestedSymbols', workspace),
      {
        headers: getApiHeaders(),
      }
    )
      .then((res) => res.json())
      .then((list) => {
        setInterestedSymbols(ensureArray(list));
      })
      .catch((err) => console.error('Failed to load Interested Symbols:', err));
  }, [workspace]);

  /* ===========================
     TOGGLES
  =========================== */
  const toggleInterestedOnly = () => {
    if (!showInterestedOnly) {
      setPrevSelectedSymbols(selectedSymbols);
      setShowActivePositionsOnly(false);
      setShowInterestedOnly(true);
    } else {
      setShowInterestedOnly(false);
      setSelectedSymbols(prevSelectedSymbols);
    }
  };

  const toggleActivePositionsOnly = () => {
    if (!showActivePositionsOnly) {
      setPrevSelectedSymbols(selectedSymbols);
      setShowInterestedOnly(false);
      setShowActivePositionsOnly(true);
    } else {
      setShowActivePositionsOnly(false);
      setSelectedSymbols(prevSelectedSymbols);
    }
  };

  /* ===========================
     FILTERING LOGIC
  =========================== */
  const effectiveSelectedSymbols = (() => {
    // override modes first
    if (showInterestedOnly) return interestedSymbols;
    if (showActivePositionsOnly) return activePositionSymbols;

    // rule filter next
    if (ruleEnabled && ruleQualifiedSymbols.length > 0) return ruleQualifiedSymbols;

    // manual multi-select
    return selectedSymbols;
  })();


  useEffect(() => {
    const filtered = signals.filter((item) => {
      const matchTF =
        selectedTimeFrames.length === 0 ||
        selectedTimeFrames.includes(String(item.timeFrame));
      const matchSym =
        effectiveSelectedSymbols.length === 0 ||
        effectiveSelectedSymbols.includes(item.symbol);
      return matchTF && matchSym;
    });
    setFilteredSignals(filtered);
  }, [signals, selectedTimeFrames, effectiveSelectedSymbols]);


  /* ===========================
     RENDER
  =========================== */
  if (loading) return <div className="loading-message">Loading signal data...</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <div className="dashboard-container">
      <div className="dashboard-filter-panel">
        {/* Row 1: Primary filters */}
        <GlobalFilterBar
          selectedContext={selectedContext}
          onContextChange={setSelectedContext}
          selectedTimeFrames={selectedTimeFrames}
          onTimeFramesChange={setSelectedTimeFrames}
          selectedSymbols={selectedSymbols}
          onSymbolsChange={(symbols) => {
            setShowInterestedOnly(false);
            setShowActivePositionsOnly(false);
            setSelectedSymbols(symbols);
          }}
        />

        {/* Row 2: Watchlist + Active Positions */}
        <div className="filter-secondary-row">
          {/* Interested Symbols */}
          <div className="filter-watchlist">
            <div className="filter-section-header">
              <span className="filter-section-title">Interested Symbols</span>
              <button
                className={`filter-toggle-btn${showInterestedOnly ? ' active' : ''}`}
                onClick={toggleInterestedOnly}
                title="Override filters to show only Interested Symbols"
              >
                {showInterestedOnly ? 'Interested: ON' : 'Show Interested'}
              </button>
            </div>
            <div className="filter-chips-wrap">
              {interestedSymbols.length === 0 && (
                <span className="filter-no-symbols">No symbols yet</span>
              )}
              {interestedSymbols.map((sym) => (
                <span key={sym} className="chip">
                  {sym}
                  <button
                    onClick={() => setInterestedSymbols((prev) => prev.filter((s) => s !== sym))}
                    className="chip-close"
                    aria-label={`Remove ${sym}`}
                  >×</button>
                </span>
              ))}
            </div>
            <div className="filter-add-row">
              <input
                className="filter-add-input"
                value={newInterestedSymbol}
                onChange={(e) => setNewInterestedSymbol(e.target.value.toUpperCase())}
                placeholder="e.g. BTCUSDT"
              />
              <button
                className="filter-add-btn"
                onClick={() => {
                  const v = newInterestedSymbol.trim();
                  if (!v) return;
                  setInterestedSymbols((prev) => Array.from(new Set([...prev, v])));
                  setNewInterestedSymbol('');
                }}
              >
                Add
              </button>
            </div>
          </div>

          {/* Active Positions */}
          <div className="filter-positions">
            <div className="filter-section-header">
              <div>
                <span className="filter-section-title">Active Position Symbols</span>
                <div className="filter-positions-list">
                  {activePositionSymbols.length ? activePositionSymbols.join(', ') : '—'}
                </div>
              </div>
              <button
                className={`filter-toggle-btn${showActivePositionsOnly ? ' active' : ''}`}
                onClick={toggleActivePositionsOnly}
                title="Override filters to show only Active Position Symbols"
              >
                {showActivePositionsOnly ? 'Positions: ON' : 'Show Positions'}
              </button>
            </div>
          </div>
        </div>

        {/* Row 3: Rule Filter */}
        <RuleFilterPanel
          signals={signals}
          enabled={ruleEnabled}
          onToggleEnabled={(v) => {
            setShowInterestedOnly(false);
            setShowActivePositionsOnly(false);
            setRuleEnabled(v);
          }}
          onQualifiedSymbolsChange={(syms) => setRuleQualifiedSymbols(syms)}
        />
      </div>

      {/* Context Views */}
      {selectedContext === 'gainers' && <TopGainersView data={signals} />}
      {selectedContext === 'losers' && <TopLosersView data={signals} />}
      {selectedContext === 'volume' && <TopVolumeView data={signals} />}
      {selectedContext === 'trend' && <TrendCombinationView data={filteredSignals} />}
      {selectedContext === 'cross' && <CrossCombinationView data={filteredSignals} />}
      {selectedContext === 'tp' && <TakeProfitView data={filteredSignals} />}
      {selectedContext === 'adx' && <TopAdxTrendView data={signals} />}
    </div>
  );
}

export default SignalDashboard;
